import { useEffect } from "react";
import { Stack, Typography, Link, Chip, Button } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { MailOrderAttachment } from "dora-contracts";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import {
  loadMailSource,
  saveMailOrderTemplateFromCargo,
} from "../../../../ducks/data/mail-orders";
import {
  selectMailOrderTemplateSaveState,
  selectMailSource,
  selectMailSourceFailed,
} from "../../../../ducks/data/mail-orders/selectors";
import { useL10n } from "../../../../l10n";

/**
 * "Kilde" panel: when a cargo was auto-created from an inbound mail, shows the
 * original mail (sender, subject, body, attachments) so the dispatcher can
 * verify the mapping right where they work.
 *
 * A cargo that didn't come from a mail is the normal case and renders nothing;
 * a cargo whose source mail we failed to load says so, because "no mail" and
 * "we couldn't ask" are not the same thing to the person checking an order.
 *
 * Mounted only when the `mail-orders` feature is on - see the order dialog.
 */
const MailSourceCard = ({ cargoId }: { cargoId: string }) => {
  const { t } = useTranslation("shipmentModal");
  const l10n = useL10n();
  const dispatch = useAppDispatch();
  const source = useSelector(selectMailSource(cargoId));
  const loadFailed = useSelector(selectMailSourceFailed(cargoId));
  const templateSaveState = useSelector(
    selectMailOrderTemplateSaveState(cargoId)
  );

  useEffect(() => {
    dispatch(loadMailSource(cargoId));
  }, [dispatch, cargoId]);

  const onSaveMailTemplateClick = () =>
    dispatch(saveMailOrderTemplateFromCargo(cargoId));

  if (loadFailed) {
    return (
      <Typography variant="caption" color="error">
        {t("cards.mailSource.loadFailed")}
      </Typography>
    );
  }

  if (!source) return null;

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <MailOutlineIcon fontSize="small" />
        <Typography variant="subtitle2">{t("cards.mailSource.title")}</Typography>
        <Chip
          size="small"
          label={t("cards.mailSource.autoCreated")}
          color="primary"
          variant="outlined"
        />
      </Stack>
      <Typography variant="body2">
        <b>{t("cards.mailSource.from")}:</b>{" "}
        {source.fromName ? `${source.fromName} <${source.fromAddress}>` : source.fromAddress}
      </Typography>
      <Typography variant="body2">
        <b>{t("cards.mailSource.subject")}:</b> {source.subject ?? "-"}
      </Typography>
      <Typography variant="body2">
        <b>{t("cards.mailSource.received")}:</b>{" "}
        {l10n.formatDateAndTime(source.receivedAt)}
      </Typography>
      {source.attachments.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {source.attachments.map((attachment) => (
            <MailAttachment key={attachment.id} attachment={attachment} />
          ))}
        </Stack>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={
            templateSaveState === "saving" || templateSaveState === "saved"
          }
          onClick={onSaveMailTemplateClick}
        >
          {templateSaveState === "saved"
            ? t("cards.mailSource.mailTemplateSaved")
            : t("cards.mailSource.saveMailTemplate")}
        </Button>
        {templateSaveState === "multipleOrdersInMail" && (
          <Typography variant="caption" color="text.secondary">
            {t("cards.mailSource.multiOrderNoMailTemplate")}
          </Typography>
        )}
        {templateSaveState === "error" && (
          <Typography variant="caption" color="error">
            {t("cards.mailSource.saveMailTemplateFailed")}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {t("cards.mailSource.saveMailTemplateHint")}
        </Typography>
      </Stack>
      {source.text && (
        <Typography
          variant="body2"
          component="pre"
          sx={{
            mt: 1,
            p: 1,
            maxHeight: 260,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            backgroundColor: "var(--gray-100, #f5f5f5)",
            borderRadius: 1,
            fontFamily: "inherit",
          }}
        >
          {source.text}
        </Typography>
      )}
    </div>
  );
};

const MailAttachment = ({
  attachment,
}: {
  attachment: MailOrderAttachment;
}) => {
  // No signed URL means the stored file is gone or couldn't be signed - show
  // the name so the dispatcher knows the mail had it, but don't fake a link.
  if (!attachment.url) {
    return (
      <Typography variant="body2" color="text.secondary">
        <AttachFileIcon fontSize="inherit" /> {attachment.filename}
      </Typography>
    );
  }
  return (
    <Link
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      underline="hover"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      <AttachFileIcon fontSize="inherit" />
      {attachment.filename}
    </Link>
  );
};

export default MailSourceCard;
